import React, { useCallback, useEffect, useState } from 'react'
import { Table, Button, message, Tag, Tooltip, Card, Space } from 'antd'
import { EditOutlined, ReloadOutlined, InfoCircleOutlined, DownloadOutlined } from '@ant-design/icons'
import { memoAPI } from '../services/api'
import { formatTime } from '../utils/timezone'
import { hasPermission } from '../utils/auth'

export default function MemoList({ onEdit, refresh, searchTerm }) {
  const [memos, setMemos] = useState([])
  const [loading, setLoading] = useState(false)
  const [pageSize, setPageSize] = useState(20)
  const [currentPage, setCurrentPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [refreshKey, setRefreshKey] = useState(0)
  const [expandedRows, setExpandedRows] = useState(new Set())
  // Use permission check function

  const fetchMemos = useCallback(async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        setLoading(false)
        message.warning('Please login first')
        window.location.href = '/login'
        return
      }
      const res = await memoAPI.getAll({ page: currentPage, limit: pageSize, search: searchTerm })
      const rows = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : [])
      const count = res.data?.pagination?.total || rows.length
      setMemos(rows)
      setTotal(count)
    } catch (error) {
      console.error('Failed to fetch memos:', error)
      if (error?.response?.status === 401) {
        message.warning('Login expired, please login again')
        localStorage.removeItem('token')
        localStorage.removeItem('userId')
        localStorage.removeItem('isAdmin')
        window.location.href = '/login'
        return
      }
      message.error(`Failed to fetch memos: ${error.response?.data?.message || error.message}`)
      // Set empty array to avoid infinite retry
      setMemos([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [currentPage, pageSize, searchTerm])

  useEffect(() => {
    // Add debounce to avoid frequent requests
    const timeoutId = setTimeout(fetchMemos, 150)
    return () => clearTimeout(timeoutId)
  }, [refresh, refreshKey, fetchMemos])

  // Reset to first page when search term changes to avoid "no data" misjudgment due to current page overflow
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1)
    message.success('Refreshing data...')
  }

  const handleExportCSV = async () => {
    try {
      setLoading(true)
      const response = await memoAPI.exportCSV()
      
      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      
      // 从响应头获取文件名，或使用默认名称
      const contentDisposition = response.headers['content-disposition']
      let fileName = 'memos-export.csv'
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/)
        if (fileNameMatch && fileNameMatch.length === 2) {
          fileName = fileNameMatch[1]
        }
      }
      
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
      message.success('CSV file exported successfully!')
    } catch (err) {
      console.error('Export CSV error:', err)
      message.error('Failed to export CSV: ' + (err.response?.data?.message || err.message))
    } finally {
      setLoading(false)
    }
  }

  const handleTableChange = (pagination) => {
    if (pagination?.pageSize && pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize)
    }
    if (pagination?.current && pagination.current !== currentPage) {
      setCurrentPage(pagination.current)
    }
  }

  const handleRowExpand = (expanded, record) => {
    const newExpandedRows = new Set(expandedRows)
    if (expanded) {
      newExpandedRows.add(record.id)
    } else {
      newExpandedRows.delete(record.id)
    }
    setExpandedRows(newExpandedRows)
  }

  // Backend search and pagination adopted, frontend no longer filters twice to avoid inconsistency between results and total
  const filteredMemos = memos

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Format time display
    return formatTime(dateString, 'YYYY-MM-DD HH:mm');
  };

  // Build columns dynamically based on user permissions
  const getColumns = () => {
    const baseColumns = [
      {
        title: <span style={{ fontWeight: 'bold', fontSize: 16 }}>NO.</span>,
        dataIndex: 'id',
        key: 'id',
        width: 80,
        fixed: 'left',
        render: (text) => <b>#{text}</b>
      },
      {
        title: <span style={{ fontWeight: 'bold', fontSize: 16 }}>Site</span>,
        dataIndex: 'site',
        key: 'site',
        width: 250,
        render: (text) => (
          <div style={{ wordBreak: 'break-all' }}>
            {text}
          </div>
        )
      },
      {
        title: <span style={{ fontWeight: 'bold', fontSize: 16 }}>States</span>,
        dataIndex: 'keyword',
        key: 'keyword',
        width: 180,
        render: (text, record) => (
          <div style={{ wordBreak: 'break-all', lineHeight: '1.2' }}>
            <div style={{ fontSize: '12px', marginBottom: '2px' }}>
              FTP: <span style={{ 
                fontWeight: 'bold', 
                color: record.ftp_user ? '#52c41a' : '#ff4d4f' 
              }}>
                {record.ftp_user ? 'yes' : 'no'}
              </span>
            </div>
            <div style={{ fontSize: '12px' }}>
              Password: <span style={{ 
                fontWeight: 'bold', 
                color: record.password ? '#52c41a' : '#ff4d4f' 
              }}>
                {record.password ? 'yes' : 'no'}
              </span>
            </div>
          </div>
        )
      },
      {
        title: <span style={{ fontWeight: 'bold', fontSize: 16 }}>Site Time</span>,
        dataIndex: 'site_time',
        key: 'site_time',
        width: 150,
        render: (_, record) => {
          if (!record.site_time) {
            return <span style={{ color: '#999', fontSize: '12px' }}>-</span>;
          }
          
          const siteTime = new Date(record.site_time);
          const formattedDate = siteTime.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          });
          const formattedTime = siteTime.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          });
          
          return (
            <Tooltip 
              placement="topLeft" 
              title={
                <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                  <div><strong>Date:</strong> {formattedDate}</div>
                  <div><strong>Time:</strong> {formattedTime}</div>
                  <div><strong>Full:</strong> {record.site_time}</div>
                </div>
              }
              mouseEnterDelay={0.05}
              destroyPopupOnHide={{ keepParent: true }}
              getPopupContainer={(trigger) => trigger}
            >
              <div style={{ 
                cursor: 'help',
                fontSize: '12px',
                lineHeight: '1.2'
              }}>
                {formattedDate}<br/>{formattedTime}
              </div>
            </Tooltip>
          );
        }
      },
      {
        title: <span style={{ fontWeight: 'bold', fontSize: 16 }}>Group</span>,
        dataIndex: 'group',
        key: 'group',
        width: 100,
        render: (text) => text ? <Tag color="green">{text}</Tag> : <span>-</span>
      },
      {
        title: <span style={{ fontWeight: 'bold', fontSize: 16 }}>Finish Status</span>,
        dataIndex: 'finish',
        key: 'finish',
        width: 120,
        render: (text) => text ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>
      },
      {
        title: <span style={{ fontWeight: 'bold', fontSize: 16 }}>Remark</span>,
        dataIndex: 'remark',
        key: 'remark',
        width: 120,
        render: (text) => text ? <Tag color="blue">{text}</Tag> : <span>-</span>
      },
      {
        title: <span style={{ fontWeight: 'bold', fontSize: 16 }}>Auditor</span>,
        dataIndex: 'auditor',
        key: 'auditor',
        width: 120,
        render: (text) => text ? <Tag color="orange">{text}</Tag> : <span>-</span>
      },
      {
        title: <span style={{ fontWeight: 'bold', fontSize: 16 }}>Details</span>,
        key: 'expand',
        width: 100,
        render: (_, record) => (
          <Button 
            type="link" 
            size="small"
            icon={<InfoCircleOutlined />}
            onClick={() => handleRowExpand(!expandedRows.has(record.id), record)}
            style={{
              padding: '4px 8px',
              fontSize: '12px'
            }}
          >
            {expandedRows.has(record.id) ? 'Collapse' : 'Details'}
          </Button>
        )
      }
    ];

    // Add Action column only for users with write permission
    if (hasPermission('memo_write')) {
      baseColumns.push({
        title: <span style={{ fontWeight: 'bold', fontSize: 16 }}>Action</span>,
        key: 'action',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Button 
            type="primary" 
            size="small"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
            style={{
              borderRadius: '6px',
              height: '28px',
              padding: '0 12px',
              fontSize: '12px',
              fontWeight: '500',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease'
            }}
          >
            EDIT
          </Button>
        )
      });
    }

    return baseColumns;
  };

  const columns = getColumns();

  return (
    <div>
      {searchTerm && (
        <div style={{ marginBottom: 16, padding: '8px 12px', backgroundColor: '#f0f2f5', borderRadius: '6px', border: '1px solid #d9d9d9' }}>
          <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
            🔍 Search Keyword: "{searchTerm}"
          </span>
          <span style={{ marginLeft: 16, color: '#666' }}>
            Found {total} results
          </span>
          {total === 0 && (
            <span style={{ marginLeft: 16, color: '#ff4d4f' }}>
              💡 Try different keywords or check spelling
            </span>
          )}
        </div>
      )}
      
    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <b>Memo list</b> (<Tag color="blue">{total}</Tag> items)
      </div>
      <Space>
        {hasPermission('super_admin') && (
          <Button 
            type="default" 
            icon={<DownloadOutlined />} 
            onClick={handleExportCSV}
            loading={loading}
            style={{
              borderRadius: '6px',
              height: '32px',
              padding: '0 16px',
              fontSize: '14px',
              fontWeight: '500',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease'
            }}
          >
            Export CSV
          </Button>
        )}
        <Button 
          type="primary" 
          icon={<ReloadOutlined />} 
          onClick={handleRefresh}
          loading={loading}
          style={{
            borderRadius: '6px',
            height: '32px',
            padding: '0 16px',
            fontSize: '14px',
            fontWeight: '500',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease'
          }}
        >
          Refresh
        </Button>
      </Space>
    </div>
    <Table
    columns={columns}
    dataSource={filteredMemos}
    rowKey="id"
    bordered
    loading={loading}
    pagination={{
      current: currentPage,
      pageSize: pageSize,
      total: total,
      pageSizeOptions: [10, 20, 50, 100],
      showSizeChanger: true,
      showTotal: (t, range) => `Items ${range[0]}-${range[1]}, total ${t} items`,
      showQuickJumper: true,
      showLessItems: false,
      size: 'default'
    }}
    onChange={handleTableChange}
    scroll={{ x: 1000 }}
    expandable={{
      expandedRowKeys: Array.from(expandedRows),
      onExpand: handleRowExpand,
      expandedRowRender: (record) => (
        <div style={{ margin: '16px 0', padding: '16px', backgroundColor: '#fafafa', borderRadius: '8px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
            <Card size="small" title="Basic Info" style={{ marginBottom: '8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
                <div><strong>ID:</strong> #{record.id}</div>
                <div><strong>Site:</strong> {record.site}</div>
                <div><strong>Keywords:</strong> {record.keyword || '-'}</div>
                <div><strong>Group:</strong> {record.group ? <Tag color="green">{record.group}</Tag> : '-'}</div>
                <div><strong>Remark:</strong> {record.remark ? <Tag color="blue">{record.remark}</Tag> : '-'}</div>
                <div><strong>Auditor:</strong> {record.auditor ? <Tag color="orange">{record.auditor}</Tag> : '-'}</div>
                <div><strong>Finish Status:</strong> {record.finish ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>}</div>
              </div>
            </Card>
            
            <Card size="small" title="Access Info" style={{ marginBottom: '8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
                <div><strong>IP:</strong> {record.ip || '-'}</div>
                <div><strong>Baota:</strong> {record.bt || '-'}</div>
                <div><strong>Backend URL:</strong> {
                  record.admin_url ? (
                    <a 
                      href={/^https?:\/\//i.test(record.admin_url) ? record.admin_url : `http://${record.admin_url}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#1890ff', wordBreak: 'break-all' }}
                    >
                      {record.admin_url}
                    </a>
                  ) : '-'
                }</div>
                <div><strong>Site Time:</strong> {formatDate(record.site_time)}</div>
                <div><strong>Add Time:</strong> {formatDate(record.createdAt)}</div>
              </div>
            </Card>
            
            <Card size="small" title="Auth Info" style={{ marginBottom: '8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
                <div><strong>Username:</strong> {record.username || '-'}</div>
                <div><strong>Password:</strong> {record.password || '-'}</div>
                <div><strong>FTP User:</strong> {record.ftp_user || '-'}</div>
                <div><strong>FTP Pass:</strong> {record.ftp_pass || '-'}</div>
              </div>
            </Card>
          </div>
          
          {hasPermission('memo_write') && (
            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <Button 
                type="primary" 
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
                style={{
                  borderRadius: '6px',
                  height: '32px',
                  padding: '0 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                Edit Record
              </Button>
            </div>
          )}
        </div>
      ),
      rowExpandable: () => true,
    }}
    />
    <style>
      {`
        .ip-with-tooltip .bt-tooltip { opacity: 0; transform: translateY(-2px); transition: opacity .15s ease, transform .15s ease; pointer-events: none; }
        .ip-with-tooltip:hover .bt-tooltip { opacity: 1; transform: translateY(0); }
      `}
    </style>
    </div>
    )
}